/**
 * Matching d'UN candidat dans la fiche : son score (vue d'ensemble) et sa
 * restitution complète (onglet de l'épreuve › Matching).
 *
 * Le candidat est situé PARMI ceux de sa campagne : le moteur est celui de
 * l'onglet Matching de la campagne (`useMatchingReferentiel`,
 * `useClassementEntStruct`), alimenté par les mêmes passations, pour que score
 * et rang soient identiques sur les deux écrans.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import type { EvaluationData } from "@repo/results";
import { Loader2 } from "lucide-react";
import type { Id } from "../../../../convex/_generated/dataModel";
import { interpreterCorrelation } from "../../../../convex/referentiel/rapport";
import { RestitutionMatchingEntStruct } from "../entstruct/RestitutionMatchingEntStruct";
import {
  useClassementEntStruct,
  type EntreeClassementEntStruct,
} from "../entstruct/useClassementEntStruct";
import { formatR } from "../referentiel/RapportMatchingCulturel";
import { RestitutionMatchingReferentiel } from "../referentiel/RestitutionMatchingReferentiel";
import {
  useMatchingReferentiel,
  type EntreeMatching,
  type EvaluationFitCulturelRow,
  type EvaluationPamiRow,
  type MatchingReferentiel,
} from "../referentiel/useMatchingReferentiel";
import type { GenreMatching } from "./epreuvesFiche";

/**
 * Passations de la campagne du candidat, par test. `undefined` = chargement
 * (ou non demandé : le portail ne charge que ce que la fiche affiche).
 */
export type ContexteCampagneFiche = {
  campagneId: Id<"campagnes">;
  /** Tient lieu de poste visé dans le rapport de matching culturel. */
  campagneNom?: string;
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  evaluationsEntStruct: EvaluationData[] | undefined;
};

type MatchingCandidat =
  | { etat: "chargement" }
  | { etat: "indisponible"; raison: string }
  | {
      etat: "referentiel";
      cible: "pami" | "pc-fit";
      matching: MatchingReferentiel;
      entree: EntreeMatching;
      rang: number;
      total: number;
    }
  | {
      etat: "bareme";
      entree: EntreeClassementEntStruct;
      /** Absent quand les barèmes de la campagne ne sont pas comparables. */
      rang?: number;
      total: number;
    };

/** Références stables : un `[]` littéral relancerait le calcul à chaque rendu. */
const AUCUNE_PAMI: EvaluationPamiRow[] = [];
const AUCUNE_FIT_CULTUREL: EvaluationFitCulturelRow[] = [];

function useMatchingCandidat(
  genre: GenreMatching,
  parcoursId: Id<"parcoursEvaluations">,
  campagne: ContexteCampagneFiche,
): MatchingCandidat {
  const cible = genre.kind === "referentiel" ? genre.cible : "pami";
  // Une cible ne compare QUE ses passations : lui passer l'autre jeu ferait
  // cohabiter des candidats notés sur des échelles différentes.
  const matching = useMatchingReferentiel({
    campagneId: campagne.campagneId,
    cible,
    actif: genre.kind === "referentiel",
    evaluationsPami: cible === "pami" ? campagne.evaluationsPami : AUCUNE_PAMI,
    evaluationsFitCulturel:
      cible === "pc-fit" ? campagne.evaluationsFitCulturel : AUCUNE_FIT_CULTUREL,
  });
  const entStruct = useClassementEntStruct(
    genre.kind === "bareme" ? campagne.evaluationsEntStruct : undefined,
  );

  if (genre.kind === "bareme") {
    if (campagne.evaluationsEntStruct === undefined) return { etat: "chargement" };
    const index = entStruct.classement.findIndex(
      (e) => e.evaluation.parcoursId === parcoursId,
    );
    const entree = entStruct.classement[index];
    if (!entree || entree.score === null) {
      return {
        etat: "indisponible",
        raison:
          "Entretien non exploitable : aucun critère du barème n'a pu être noté.",
      };
    }
    return {
      etat: "bareme",
      entree,
      rang: entStruct.baremesHeterogenes ? undefined : index + 1,
      total: entStruct.classement.length,
    };
  }

  if (matching.chargement || matching.etat === undefined) {
    return { etat: "chargement" };
  }
  if (matching.etat === null) {
    return {
      etat: "indisponible",
      raison:
        "Aucun référentiel n'est configuré sur cette campagne : pas de comparaison possible.",
    };
  }
  if (!matching.version) {
    return {
      etat: "indisponible",
      raison:
        "Aucun référent n'a encore contribué : il n'y a pas de référentiel contre lequel comparer ce candidat.",
    };
  }
  const index = matching.classement.findIndex(
    (e) => e.row.parcoursId === parcoursId,
  );
  const entree = matching.classement[index];
  if (!entree || entree.comparaison === null) {
    return {
      etat: "indisponible",
      raison:
        genre.cible === "pami"
          ? "PAMI non exploitable : les scores du candidat sont absents."
          : "Passation incomplète : pas de comparaison possible.",
    };
  }
  return {
    etat: "referentiel",
    cible: genre.cible,
    matching,
    entree,
    rang: index + 1,
    total: matching.classement.length,
  };
}

/** Score de matching en une ligne, pour la vue d'ensemble. */
export function ScoreMatchingCandidat({
  genre,
  parcoursId,
  campagne,
}: {
  genre: GenreMatching;
  parcoursId: Id<"parcoursEvaluations">;
  campagne: ContexteCampagneFiche;
}) {
  const m = useMatchingCandidat(genre, parcoursId, campagne);

  if (m.etat === "chargement") {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  }
  if (m.etat === "indisponible") {
    return <span className="text-xs text-muted-foreground">Pas de matching</span>;
  }

  const score =
    m.etat === "referentiel" ? m.entree.comparaison?.scoreMatching ?? null : m.entree.score;
  if (score === null) {
    return <span className="text-xs text-muted-foreground">Score non défini</span>;
  }

  const estCorrelation = m.etat === "referentiel" && m.cible === "pc-fit";
  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="text-sm font-semibold tabular-nums">
        {estCorrelation ? `r ${formatR(score)}` : `${Math.round(score)} / 100`}
      </span>
      <span className="text-xs text-muted-foreground">
        {estCorrelation &&
          m.etat === "referentiel" &&
          `${interpreterCorrelation(m.entree.comparaison?.correlation ?? null).libelle} · `}
        {m.rang !== undefined
          ? `rang ${m.rang} sur ${m.total}`
          : "barèmes non comparables"}
      </span>
    </span>
  );
}

/** Restitution complète, onglet de l'épreuve › Matching. */
export function MatchingEpreuve({
  genre,
  parcoursId,
  campagne,
  token,
  convexUrl,
}: {
  genre: GenreMatching;
  parcoursId: Id<"parcoursEvaluations">;
  campagne: ContexteCampagneFiche;
  token?: string | null;
  convexUrl?: string;
}) {
  const m = useMatchingCandidat(genre, parcoursId, campagne);

  if (m.etat === "chargement") {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (m.etat === "indisponible") {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {m.raison}
        </CardContent>
      </Card>
    );
  }
  if (m.etat === "referentiel") {
    return (
      <div className="space-y-6">
        <RestitutionMatchingReferentiel
          matching={m.matching}
          entree={m.entree}
          campagneNom={campagne.campagneNom}
          token={token}
          convexUrl={convexUrl}
        />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Score global pondéré</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-end gap-3">
            <span className="text-4xl leading-none font-bold tabular-nums">
              {Math.round(m.entree.score ?? 0)}
            </span>
            <span className="pb-1 text-sm text-muted-foreground">/ 100</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {m.rang !== undefined
              ? `Rang ${m.rang} sur ${m.total} dans cette campagne.`
              : "Les candidats de cette campagne n'ont pas passé le même entretien : pas de rang."}
          </p>
        </CardContent>
      </Card>
      <RestitutionMatchingEntStruct entree={m.entree} />
    </div>
  );
}
